import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { Check, Crown, Heart, Loader2, Star, Gift } from 'lucide-react';
import AppLogo from '../components/AppLogo';

const BillingPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [basicPeriod, setBasicPeriod] = useState('monthly');
  const [basicPlusPeriod, setBasicPlusPeriod] = useState('monthly');
  const [coachPeriod, setCoachPeriod] = useState('monthly');
  const [promoCode, setPromoCode] = useState('');
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [donationAmount, setDonationAmount] = useState('25');
  const [donateLoading, setDonateLoading] = useState(false);

  const handleDonate = async () => {
    const amt = parseFloat(donationAmount);
    if (isNaN(amt) || amt < 1) { toast.error('Please enter an amount of at least $1'); return; }
    setDonateLoading(true);
    try {
      const res = await axios.post(`${API}/billing/donate`, { amount: amt, origin_url: window.location.origin });
      if (res.data.url) window.location.href = res.data.url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start donation'); }
    setDonateLoading(false);
  };

  useEffect(() => { fetchBillingData(); }, []);

  const fetchBillingData = async () => {
    try {
      const statusRes = await axios.get(`${API}/billing/status`);
      setCurrentPlan(statusRes.data);
    } catch (error) {
      toast.error('Failed to load billing info');
    } finally { setLoading(false); }
  };

  const handleSubscribe = async (planId, billingPeriod = 'monthly') => {
    setCheckoutLoading(planId);
    try {
      const originUrl = window.location.origin;
      const res = await axios.post(`${API}/billing/checkout`, { plan_id: planId, origin_url: originUrl, billing_period: billingPeriod });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
    } finally { setCheckoutLoading(null); }
  };

  const handleRedeem = async () => {
    if (!promoCode.trim()) { toast.error('Please enter a code'); return; }
    setRedeemLoading(true);
    try {
      const res = await axios.post(`${API}/billing/redeem`, { code: promoCode });
      toast.success(res.data.message);
      setPromoCode('');
      fetchBillingData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally { setRedeemLoading(false); }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel your subscription? You can resubscribe anytime.')) return;
    setCancelLoading(true);
    try {
      const res = await axios.post(`${API}/billing/cancel`);
      toast.success(res.data.message || 'Subscription canceled.');
      fetchBillingData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to cancel';
      // Friendlier messaging when there's actually nothing to cancel
      if (msg.toLowerCase().includes('no active subscription')) {
        toast.info("You don't have a paid subscription right now — nothing to cancel.");
      } else {
        toast.error(msg);
      }
    } finally { setCancelLoading(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" data-testid="billing-page">
      <header className="surface-header sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-5 py-3.5 flex items-center gap-3">
          <AppLogo />
          <div>
            <h1 className="text-xl font-bold text-foreground tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Support CircleUpSquad</h1>
            <p className="text-xs text-muted-foreground">Free for your first 5 supporters</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-8">
        {/* Current Plan Banner */}
        {currentPlan?.has_subscription && (
          <div className="surface-card rounded-xl p-5 mb-8 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Current Plan</p>
              <p className="text-lg font-bold text-foreground">{currentPlan.plan_name}</p>
              <p className="text-xs text-muted-foreground">Active since {new Date(currentPlan.subscribed_at).toLocaleDateString()}</p>
            </div>
            <Button variant="outline" size="sm" className="text-destructive hover:text-destructive border-destructive/30 hover:border-destructive/50" onClick={handleCancel} disabled={cancelLoading} data-testid="cancel-plan-btn">
              {cancelLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Cancel Plan
            </Button>
          </div>
        )}

        <div className="text-center mb-8">
          <div className="relative w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <div className="absolute inset-0 rounded-full border-2 border-cyan-300/40 animate-pulse" />
            <div className="absolute inset-2 rounded-full border-2 border-cyan-400/60" />
            <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
              <Heart className="w-4 h-4 text-white" fill="currentColor" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Free for your first 5 supporters</h2>
          <p className="text-muted-foreground max-w-md mx-auto">
            CircleUpSquad is free to use for circles up to 5 members. If it{"'"}s helped, please consider a donation so we can keep it free for more families.
          </p>
        </div>

        {/* Donate */}
        <div className="surface-card rounded-2xl p-6 max-w-lg mx-auto mb-6 border-2 border-cyan-100 dark:border-cyan-900/40 bg-gradient-to-br from-cyan-50/40 to-transparent dark:from-cyan-950/20" data-testid="donation-section">
          <div className="flex items-center gap-2 mb-3">
            <Heart className="w-5 h-5 text-cyan-600" fill="currentColor" />
            <h3 className="text-base font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>Donate to CircleUpSquad</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-4">Every dollar helps us support more families navigating cancer and serious illness.</p>
          <div className="grid grid-cols-4 gap-2 mb-3">
            {[10, 25, 50, 100].map(v => (
              <button key={v}
                onClick={() => setDonationAmount(String(v))}
                className={`py-2.5 rounded-lg text-sm font-semibold transition-colors ${donationAmount === String(v) ? 'bg-cyan-600 text-white' : 'bg-muted text-foreground hover:bg-muted/80'}`}
                data-testid={`donate-preset-${v}`}
              >
                ${v}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <div className="flex-1 flex items-center border border-input rounded-lg bg-background overflow-hidden">
              <span className="px-3 text-muted-foreground">$</span>
              <input
                type="number" min="1" step="1"
                value={donationAmount}
                onChange={e => setDonationAmount(e.target.value)}
                placeholder="Custom amount"
                className="flex-1 py-2 pr-3 bg-transparent text-sm text-foreground focus:outline-none"
                data-testid="donate-amount-input"
              />
            </div>
            <Button className="btn-primary" onClick={handleDonate} disabled={donateLoading} data-testid="donate-btn">
              {donateLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Heart className="w-4 h-4 mr-2" />}
              Donate
            </Button>
          </div>
        </div>


        {/* Promo / Gift Code */}
        <div className="mt-8 max-w-md mx-auto space-y-4">
          <button
            onClick={() => navigate('/coach')}
            className="w-full surface-card rounded-xl p-5 text-center hover:border-cyan-300 dark:hover:border-cyan-700 transition-colors"
            data-testid="try-coaching-btn"
          >
            <p className="text-base font-semibold text-foreground">Want to try coaching?</p>
            <p className="text-xs text-muted-foreground mt-1">Meet Shannon — first session is free</p>
          </button>

          <div className="surface-card rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <Gift className="w-5 h-5 text-cyan-600" />
              <h3 className="text-sm font-semibold text-foreground">Have a gift or promo code?</h3>
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                placeholder="Enter code"
                className="flex-1 px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-cyan-500"
                data-testid="promo-code-input"
              />
              <Button onClick={handleRedeem} disabled={redeemLoading} className="btn-primary" data-testid="redeem-code-btn">
                {redeemLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Redeem'}
              </Button>
            </div>
          </div>

          {/* Always-visible cancel fallback — for users whose subscription data may be
              inconsistent but who know they're on a paid plan. Backend safely rejects
              cancels for accounts without an active subscription. */}
          <div className="text-center pt-2" data-testid="cancel-fallback">
            <button
              onClick={handleCancel}
              disabled={cancelLoading}
              className="text-sm text-muted-foreground hover:text-destructive transition-colors underline underline-offset-4"
              data-testid="cancel-fallback-btn"
            >
              {cancelLoading ? 'Canceling...' : 'Have an active paid plan? Cancel here'}
            </button>
            <p className="text-[11px] text-muted-foreground mt-1">If you don{"'"}t have a paid plan, nothing will change.</p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default BillingPage;
